import { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { supabase } from "../supabase";
import { TARGETS, CONCEPTS } from "../utils/buildPrompt";

const IconBack = () => (
  <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
    <path d="M11.625 15.7917L5.83333 10L11.625 4.20833L12.8125 5.39583L8.20833 10L12.8125 14.6042L11.625 15.7917Z" fill="#111111"/>
  </svg>
);

const fmtDate = (str) => {
  if (!str) return '-';
  const d = new Date(str);
  return `${String(d.getMonth() + 1).padStart(2, '0')}.${String(d.getDate()).padStart(2, '0')} ${String(d.getHours()).padStart(2, '0')}:${String(d.getMinutes()).padStart(2, '0')}`;
};
const num = (v) => (v === null || v === undefined ? '-' : v.toLocaleString());
const won = (v) => `${num(v)}원`;
// 이메일은 관리자만 보더라도 화면에 통째로 남기지 않는다.
const maskEmail = (e) => {
  const [id, domain] = String(e).split('@');
  if (!domain) return e;
  return `${id.slice(0, 3)}${'*'.repeat(Math.max(1, id.length - 3))}@${domain}`;
};

function Card({ title, children }) {
  return (
    <div style={{ background: '#ffffff', border: '1px solid #111111', borderRadius: 12, overflow: 'hidden' }}>
      <div style={{ background: '#111111', color: '#ffffff', padding: '10px 14px', borderRadius: '12px 12px 0 0', fontSize: 16, fontWeight: 700, letterSpacing: 1 }}>
        {title}
      </div>
      <div style={{ padding: 14 }}>{children}</div>
    </div>
  );
}

// 핵심 숫자 한 줄. 큰 값 하나 + 라벨.
function Stat({ label, value, sub }) {
  return (
    <div style={{ flex: 1, minWidth: 0 }}>
      <div style={{ fontSize: 13, color: '#888888', marginBottom: 4, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{label}</div>
      <div style={{ fontSize: 22, fontWeight: 700, color: '#111111', lineHeight: 1.2 }}>{value}</div>
      {sub && <div style={{ fontSize: 13, color: '#888888', marginTop: 2 }}>{sub}</div>}
    </div>
  );
}

function Row({ left, right, muted }) {
  return (
    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 12, padding: '8px 0', borderTop: '1px solid #eeeeee' }}>
      <span style={{ fontSize: 15, color: muted ? '#888888' : '#111111', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{left}</span>
      <span style={{ fontSize: 15, color: '#111111', fontWeight: 600, whiteSpace: 'nowrap', flexShrink: 0 }}>{right}</span>
    </div>
  );
}

// 비율 막대 — 인기 대상/컨셉을 눈으로 비교하기 위한 최소 장치
function Bar({ label, count, max }) {
  const pct = max > 0 ? Math.round((count / max) * 100) : 0;
  return (
    <div style={{ padding: '8px 0', borderTop: '1px solid #eeeeee' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 6 }}>
        <span style={{ fontSize: 15, color: '#111111' }}>{label}</span>
        <span style={{ fontSize: 15, color: '#111111', fontWeight: 600 }}>{num(count)}</span>
      </div>
      <div style={{ height: 6, background: '#f0f0f0', borderRadius: 3, overflow: 'hidden' }}>
        <div style={{ width: `${pct}%`, height: '100%', background: '#111111' }} />
      </div>
    </div>
  );
}

export default function AdminPage({ user }) {
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const [loggedInAs, setLoggedInAs] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    setLoggedInAs(null);
    try {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) {
        setError('로그인이 필요해요.');
        setLoading(false);
        return;
      }
      const res = await fetch('/api/admin-stats', {
        headers: { Authorization: `Bearer ${session.access_token}` },
      });
      const json = await res.json();
      if (!res.ok) {
        setError(json.error || '통계를 불러오지 못했어요.');
        setLoggedInAs(json.loggedInAs || null);
      } else {
        setData(json);
      }
    } catch {
      setError('네트워크 오류가 났어요.');
    }
    setLoading(false);
  }, []);

  useEffect(() => { load(); }, [load]);

  const targetMax = data?.conversions.topTargets[0]?.count || 0;
  const conceptMax = data?.conversions.topConcepts[0]?.count || 0;

  return (
    <div style={{ minHeight: '100vh', background: '#f0f0f0', display: 'flex', justifyContent: 'center' }}>
      <div style={{ width: '100%', maxWidth: 430, background: '#ffffff', minHeight: '100vh', display: 'flex', flexDirection: 'column', fontFamily: "'DM Mono', monospace" }}>

        <div style={{ background: '#ffffff', borderBottom: '1px solid #111111', height: 52, display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '0 16px', flexShrink: 0 }}>
          <button onClick={() => navigate('/')} style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 0, display: 'flex', alignItems: 'center' }}>
            <IconBack />
          </button>
          <span style={{ fontSize: 16, fontWeight: 700, color: '#111111', letterSpacing: 1 }}>운영 현황</span>
          <button onClick={load} disabled={loading}
            style={{ background: 'none', border: 'none', cursor: loading ? 'default' : 'pointer', padding: 0, fontSize: 15, color: loading ? '#bbbbbb' : '#111111', fontFamily: "'DM Mono', monospace" }}>
            ↻
          </button>
        </div>

        <div style={{ padding: '12px 16px', display: 'flex', flexDirection: 'column', gap: 12, flex: 1 }}>

          {loading && (
            <div style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 10, fontSize: 15, color: '#888888' }}>
              <span style={{ width: 16, height: 16, border: '2px solid #bbbbbb', borderTopColor: '#111111', borderRadius: '50%', display: 'inline-block', animation: 'spin .7s linear infinite' }} />
              불러오는 중이에요
            </div>
          )}

          {!loading && error && (
            <div style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <div style={{ textAlign: 'center', padding: '0 16px' }}>
                <div style={{ fontSize: 15, color: '#111111', fontWeight: 600, marginBottom: 8 }}>{error}</div>
                {loggedInAs && (
                  <div style={{ border: '1px solid #111111', borderRadius: 12, overflow: 'hidden', marginBottom: 12, textAlign: 'left' }}>
                    <div style={{ background: '#111111', color: '#ffffff', padding: '10px 14px', fontSize: 16, fontWeight: 700, letterSpacing: 1 }}>
                      지금 로그인된 계정
                    </div>
                    <div style={{ padding: 14 }}>
                      <div style={{ fontSize: 16, color: '#111111', fontWeight: 600, wordBreak: 'break-all', marginBottom: 8 }}>
                        {loggedInAs}
                      </div>
                      <div style={{ fontSize: 13, color: '#888888', lineHeight: 1.7 }}>
                        이 이메일을 Vercel 환경변수 <strong style={{ color: '#111111' }}>ADMIN_EMAILS</strong>에 넣고
                        재배포하면 접근할 수 있어요.
                      </div>
                    </div>
                  </div>
                )}
                {!user && (
                  <button onClick={() => navigate('/login')}
                    style={{ background: '#111111', color: '#ffffff', border: 'none', borderRadius: 8, padding: '11px 20px', fontSize: 15, fontWeight: 700, cursor: 'pointer', fontFamily: "'DM Mono', monospace" }}>
                    로그인하기
                  </button>
                )}
              </div>
            </div>
          )}

          {!loading && !error && data && (
            <>
              <Card title="가입자">
                <div style={{ display: 'flex', gap: 12 }}>
                  <Stat label="전체" value={num(data.users.total)} />
                  <Stat label="최근 7일" value={num(data.users.last7)} />
                  <Stat label="최근 30일" value={num(data.users.last30)} />
                </div>
                {data.users.recent.length > 0 && (
                  <div style={{ marginTop: 12 }}>
                    <div style={{ fontSize: 13, color: '#888888', marginBottom: 4 }}>최근 가입</div>
                    {data.users.recent.map((u, i) => (
                      <Row key={i} left={maskEmail(u.email)} right={`${u.provider} · ${fmtDate(u.created_at)}`} />
                    ))}
                  </div>
                )}
              </Card>

              <Card title="매출">
                <div style={{ display: 'flex', gap: 12 }}>
                  <Stat label="누적 매출" value={won(data.payments.revenue)} sub={`${num(data.payments.count)}건`} />
                  <Stat label="최근 7일" value={won(data.payments.revenue7)} sub={`${num(data.payments.count7)}건`} />
                </div>
                <div style={{ marginTop: 12 }}>
                  <Row left="결제한 사람" right={`${num(data.payments.payingUsers)}명`} />
                  <Row
                    left="결제 전환율"
                    right={data.users.total > 0 ? `${((data.payments.payingUsers / data.users.total) * 100).toFixed(1)}%` : '-'}
                  />
                  <Row left="최근 30일 매출" right={won(data.payments.revenue30)} />
                </div>
                {data.payments.recent.length > 0 && (
                  <div style={{ marginTop: 12 }}>
                    <div style={{ fontSize: 13, color: '#888888', marginBottom: 4 }}>최근 결제</div>
                    {data.payments.recent.map((p, i) => (
                      <Row key={i} left={`${p.package_name} · ${fmtDate(p.created_at)}`} right={won(p.amount)} />
                    ))}
                  </div>
                )}
              </Card>

              <Card title="변환">
                <div style={{ display: 'flex', gap: 12 }}>
                  <Stat label="누적 변환" value={num(data.conversions.total)} />
                  <Stat label="최근 7일" value={num(data.conversions.last7)} />
                  <Stat label="오늘 비로그인" value={num(data.conversions.guestToday)} />
                </div>

                {data.conversions.topTargets.length > 0 && (
                  <div style={{ marginTop: 12 }}>
                    <div style={{ fontSize: 13, color: '#888888', marginBottom: 4 }}>인기 대상</div>
                    {data.conversions.topTargets.map(t => (
                      <Bar key={t.key} label={TARGETS[t.key]?.label || t.key} count={t.count} max={targetMax} />
                    ))}
                  </div>
                )}

                {data.conversions.topConcepts.length > 0 && (
                  <div style={{ marginTop: 12 }}>
                    <div style={{ fontSize: 13, color: '#888888', marginBottom: 4 }}>인기 컨셉</div>
                    {data.conversions.topConcepts.map(c => (
                      <Bar key={c.key} label={CONCEPTS[c.key]?.label || c.key} count={c.count} max={conceptMax} />
                    ))}
                  </div>
                )}
              </Card>

              <Card title="사용 지표">
                <Row left="즐겨찾기 저장" right={`${num(data.engagement.favorites)}개`} />
                <Row left="공유 생성" right={`${num(data.engagement.shares)}건`} />
                <Row left="문의" right={`${num(data.engagement.inquiries)}건`} />
              </Card>

              <div style={{ fontSize: 13, color: '#bbbbbb', textAlign: 'center', padding: '4px 0 12px' }}>
                {fmtDate(data.generatedAt)} 기준
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
